import {
    createLauraAudit,
    patchLauraP0Context,
    patchLauraStageContext,
    updateLauraDeliverable,
} from "@/app/lib/auditflowApi";
import type { LauraPlanningStageId } from "@/app/lib/auditflowApi";
import {
    NHAI_DELIVERABLES,
    NHAI_P0_INITIAL_COMMENTS,
    NHAI_P1_FORM,
    NHAI_P2_FORM,
    NHAI_P3_FORM,
    NHAI_P4_FORM,
    NHAI_P5_FORM,
    NHAI_P6_FORM,
    NHAI_P7_FORM,
    NHAI_SAMPLE_TITLE,
} from "./lauraSampleNhaiData";

const STAGE_FORMS: Record<
    LauraPlanningStageId,
    Record<string, unknown>
> = {
    p1: NHAI_P1_FORM,
    p2: NHAI_P2_FORM,
    p3: NHAI_P3_FORM,
    p4: NHAI_P4_FORM,
    p5: NHAI_P5_FORM,
    p6: NHAI_P6_FORM,
    p7: NHAI_P7_FORM,
};

const STAGE_VALIDATION: Record<LauraPlanningStageId, { id: string; text: string; checked: boolean }[]> = {
    p1: [
        { id: "v1", text: "Audit objective is SMART", checked: false },
        { id: "v2", text: "Criteria sources are appropriate", checked: false },
        { id: "v3", text: "Out-of-scope is correctly drawn", checked: false },
    ],
    p2: [
        { id: "v1", text: "Financial figures confirmed", checked: false },
        { id: "v2", text: "Process map reflects actual operations", checked: false },
        { id: "v3", text: "Leakage vectors are complete", checked: false },
    ],
    p3: [
        { id: "v1", text: "Risks cover main failure vectors", checked: false },
        { id: "v2", text: "RIS codes ready for P4 cross-reference", checked: false },
        { id: "v3", text: "Priority tiers are defensible", checked: false },
    ],
    p4: [
        { id: "v1", text: "All Tier 1 risks mapped to questions", checked: false },
        { id: "v2", text: "ADM consistency gate resolved", checked: false },
    ],
    p5: [
        { id: "v1", text: "All QSTs covered by instruments", checked: false },
        { id: "v2", text: "Sampling plan documented", checked: false },
    ],
    p6: [
        { id: "v1", text: "Executive summary ≤ 2 pages equivalent", checked: false },
        { id: "v2", text: "Timeline is feasible", checked: false },
    ],
    p7: [
        { id: "v1", text: "Quality gate reviewed by supervisor", checked: false },
        { id: "v2", text: "All flagged items have owners", checked: false },
    ],
};

export async function seedLauraNhaiSampleAudit(payload: {
    project_id: string;
    sai_profile_id: string;
    output_language?: string;
}): Promise<string> {
    const audit = await createLauraAudit({
        title: NHAI_SAMPLE_TITLE,
        sai_profile_id: payload.sai_profile_id,
        output_language: payload.output_language ?? "en",
        project_id: payload.project_id,
    });

    await patchLauraP0Context(audit.id, {
        initial_comments: NHAI_P0_INITIAL_COMMENTS,
    });
    await updateLauraDeliverable(audit.id, "p0", NHAI_DELIVERABLES.p0);

    const stages = Object.keys(STAGE_FORMS) as LauraPlanningStageId[];
    for (const stage of stages) {
        await patchLauraStageContext(audit.id, stage, {
            form_data: STAGE_FORMS[stage],
            validation_items: STAGE_VALIDATION[stage],
            status: "generated",
        });
        await updateLauraDeliverable(
            audit.id,
            stage,
            NHAI_DELIVERABLES[stage] ?? "",
        );
    }

    return audit.id;
}
